import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { generateSHA256 } from '../utils/hashUtils';

interface HashDisplayProps {
  /** Input text to generate hash from */
  text: string;
  /** Width and height of the display container */
  size?: number | { xs: number; sm: number; md: number };
  /** Number of characters per row in the grid */
  charsPerRow?: number;
}

/**
 * HashDisplay component that shows SHA-256 hash in a grid format
 * within a rounded square container, matching the hashatar styling
 */
export default function HashDisplay({ 
  text, 
  size = 200, 
  charsPerRow = 8 
}: HashDisplayProps) {
  // Get the actual size value for calculations (use md size if responsive)
  const actualSize = typeof size === 'object' ? size.md : size;
  const [hash, setHash] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Generate hash when text changes
  useEffect(() => {
    const generateHash = async () => {
      setIsLoading(true);
      try {
        const newHash = await generateSHA256(text);
        setHash(newHash);
      } catch (error) {
        console.error('Error generating hash:', error);
        setHash('Error generating hash');
      } finally {
        setIsLoading(false);
      }
    };

    if (text) {
      generateHash();
    } else {
      setHash('');
      setIsLoading(false);
    }
  }, [text]);

  // Note: We now render individual characters directly in the grid

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '2px solid rgba(0, 0, 0, 0.1)',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        boxSizing: 'border-box',
      }}
    >
      {isLoading ? (
        <Typography
          variant="body2"
          sx={{
            color: '#00ff41',
            fontFamily: 'Monaco, "Lucida Console", monospace',
            textAlign: 'center',
          }}
        >
          Generating hash...
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${charsPerRow}, 1fr)`,
            gridTemplateRows: `repeat(${Math.ceil(hash.length / charsPerRow)}, 1fr)`,
            gap: 0,
            width: '100%',
            height: '100%',
            padding: 1,
            boxSizing: 'border-box',
          }}
        >
          {hash.split('').map((char, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00ff41',
                fontFamily: 'Monaco, "Lucida Console", monospace',
                fontSize: `${Math.max(8, actualSize / 20)}px`,
                textShadow: '0 0 5px rgba(0, 255, 65, 0.5)',
                fontWeight: 'bold',
                textAlign: 'center',
                border: '1px solid rgba(0, 255, 65, 0.1)',
                borderRadius: '2px',
                backgroundColor: 'rgba(0, 255, 65, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 255, 65, 0.1)',
                  transform: 'scale(1.1)',
                  zIndex: 1,
                },
              }}
            >
              {char}
            </Box>
          ))}
          
          {/* Hash info overlay at bottom */}
          <Box
            sx={{
              gridColumn: `1 / ${charsPerRow + 1}`,
              gridRow: `${Math.ceil(hash.length / charsPerRow) + 1}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontFamily: 'Monaco, "Lucida Console", monospace',
                fontSize: `${Math.max(6, actualSize / 30)}px`,
                textAlign: 'center',
                opacity: 0.7,
              }}
            >
              SHA-256
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
